<script lang="ts">
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	//Helpers
	import { capFirstChar, passwordHasAllMandatorySymbols } from '../../../helpers/utilities';
	import { createAsyncAction } from 'src/helpers/async_action_guard';
	import { reloginAfterPasswordChange } from 'src/helpers/change_password_helpers';
	import { validateSignUpPassword } from 'src/helpers/signup_helpers';
	//Design system components
	import { ButtonV2, InputV2, Text, toast } from '@Shelly/shelly-design-system';
	import type { InputPropsTypes } from '@Shelly/shelly-design-system';
	// Types
	import PasswordUtility from 'src/components/PasswordUtilityV2.svelte';
	import type { ChangePasswordRequest } from '../../../types/requests/settings/user/change_password';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	let currentPassValue = '',
		currentPassError: string = '',
		newPassError: string = '';

	const { loading: isSaving, run: runSave } = createAsyncAction();

	export let isMobile = false;

	let inputProps: {
		size: InputPropsTypes['size'];
		icon: InputPropsTypes['icon'] | null;
		value: InputPropsTypes['value'];
		label: InputPropsTypes['label'];
		type: InputPropsTypes['type'];
		disabled: InputPropsTypes['disabled'];
		useV2Components: boolean;
		class: string;
		autocomplete: string;
	} = {
		icon: null,
		size: 'l',
		class: '',
		disabled: false,
		value: '',
		label: '',
		type: 'password',
		useV2Components: true,
		autocomplete: 'new-password',
	};

	$: error = newPassError;

	const handleChangePassword = async (oldPass, newPass) => {
		const passwordError = validateSignUpPassword(newPass, $format);
		if (passwordError) {
			newPassError = passwordError;
			return;
		}

		await runSave(async () => {
			const res: ChangePasswordRequest = await postReq('/user/change_password', {
				old_password: window.btoa(oldPass),
				new_password: window.btoa(newPass),
			});

			if (res.isok) {
				await reloginAfterPasswordChange(newPass);
			} else if (res?.errors?.wrong_old_password) {
				currentPassError = $format('toast_error_password_change_wrong_current_pass', {
					default: 'Wrong password.',
				});
			} else if (res.errors?.spwd) {
				newPassError = $format('toast_error_password_change_same_password', {
					default: 'Choose a password you haven’t used before.',
				});
			} else {
				toast.open({ type: 'danger', title: $format('setting_err_toast') });
			}
		});
	};

	const clearErrors = () => {
		if (currentPassError) {
			currentPassError = '';
		}
		if (newPassError) {
			newPassError = '';
		}
	};
</script>

<div
	class={classMap({
		'd-flex align-items-start gap-m justify-content-between mb-m': true,
		'flex-direction-column': isMobile,
	})}
>
	<div class="width-full">
		<Text styleType="body-medium" class="mb-s">
			{capFirstChar($format('placeholder_current_password'))}
		</Text>
		<InputV2
			class="width-full"
			backgroundColor="color-00"
			icon={null}
			aria-label={capFirstChar($format('placeholder_current_password'))}
			size="l"
			bind:value={currentPassValue}
			type={'password'}
			error={currentPassError}
			on:click={() => clearErrors()}
			id="input-ph5v21d"
			a11yProps={{
				iconButtonAriaLabel: $format('button_show_password', {
					default: 'Show password',
				}),
			}}
		/>
	</div>

	<div class="width-full">
		<Text styleType="body-medium" class="mb-s">
			{capFirstChar($format('placeholder_new_password'))}
		</Text>
		<PasswordUtility
			bind:inputProps
			bind:error
			dataTestId="change-password-new-password-input"
			on:click={() => clearErrors()}
		/>
	</div>
</div>

<div class="d-flex justify-content-end">
	<ButtonV2
		size="m"
		variant="primary"
		class={classMap({
			'width-s': !isMobile,
			'width-full': isMobile,
		})}
		disabled={$isSaving || !passwordHasAllMandatorySymbols(inputProps.value)}
		loading={$isSaving}
		on:click={() => handleChangePassword(currentPassValue, inputProps.value)}
	>
		{$format('button_save')}
	</ButtonV2>
</div>
